// game/gloom.ts — the drain-clock, modernised (DESIGN §3.1).
// Fills with time spent in *unfinished combat rooms* only. It never touches HP;
// it escalates pressure: faster generators, faster enemies, then an endless
// gloomspawn trickle. Resets per zone.

import type { ZoneConfig } from '../types'
import { TUNING } from '../data/tuning'
import type { Run } from './game'

export const GLOOMSPAWN_ID = 'gloomspawn'

export class Gloom {
  value = 0
  /** Zone mechanics and relics scale the fill rate through this. */
  rateMul = 1

  private fillSeconds = 90
  private trickleTimer = 0
  private trickleInterval: number = TUNING.gloom.arrivalTrickleSeconds
  private saidRestless = false
  private saidSeething = false
  private saidArrival = false

  reset(zone: ZoneConfig): void {
    this.value = 0
    this.rateMul = 1
    this.fillSeconds = Math.max(5, zone.gloomFillSeconds)
    this.trickleTimer = 0
    this.trickleInterval = TUNING.gloom.arrivalTrickleSeconds
    this.saidRestless = false
    this.saidSeething = false
    this.saidArrival = false
  }

  get restless(): boolean {
    return this.value >= TUNING.gloom.restlessAt
  }
  get seething(): boolean {
    return this.value >= TUNING.gloom.seethingAt
  }
  get arrived(): boolean {
    return this.value >= 1
  }

  /** Generator interval divisor. */
  generatorRateMul(): number {
    return this.restless ? TUNING.gloom.restlessRateMul : 1
  }
  enemySpeedMul(): number {
    return this.seething ? TUNING.gloom.seethingSpeedMul : 1
  }
  capMul(): number {
    return this.seething ? TUNING.gloom.seethingCapMul : 1
  }

  add(frac: number): void {
    this.value = Math.max(0, Math.min(1, this.value + frac))
  }

  update(run: Run, dt: number): void {
    const fills = !run.room.cleared && !run.room.isRespite && !run.isBossRoom
    if (fills && this.value < 1) {
      this.value = Math.min(1, this.value + (dt / this.fillSeconds) * this.rateMul)
    }

    if (!this.saidRestless && this.restless) {
      this.saidRestless = true
      run.bark('The deep grows restless.')
    }
    if (!this.saidSeething && this.seething) {
      this.saidSeething = true
      run.bark('It is seething now. Move.')
      run.shake(2, 0.3)
    }
    if (!this.saidArrival && this.arrived) {
      this.saidArrival = true
      run.bark('The Gloom has arrived. It will not stop.')
      run.shake(5, 0.6)
      run.sfx('door')
    }

    if (this.arrived && fills) {
      this.trickleTimer -= dt
      if (this.trickleTimer <= 0) {
        this.trickleTimer = this.trickleInterval
        this.trickleInterval = Math.max(0.35, this.trickleInterval * TUNING.gloom.trickleAccel)
        run.spawnGloomspawn()
      }
    }
  }
}
